import { Injectable, WritableSignal, inject, signal } from '@angular/core';
import { LocalStorageService } from './local-storage.service';
import { OrderResponse, ProductOrder, ProductOrderToSave, ShopBundleList, UpdateOrderParams } from './types/order.type';
import { LOCALSTORAGE_KEYS } from './constants/databases';
import { FirebaseCrudService } from './firebase-crud.service';
import { Session } from './types/session.type';
import { ApiService } from './api.service';
import { RequestHandlerParams } from './types/api-service.types';
import { ApiResponse } from './common/types';
import { filter, map, Observable, of, tap } from 'rxjs';
import { NotificationService } from './notification/notification.service';
import { DeliveriesService } from '../settings/account/deliveries/deliveries.service';
import { StockService } from '../stock/stock.service';
import { ModalContentService } from './modal-content/modal-content.service';
import { ModalContentTypes } from './constants/modal-content-types';
import { arrayToMapMultiKey, mapToArray } from './common/utils';
import { DateTime } from 'luxon';
import { environment } from '../../environments/environment';
import { MarketStatus } from './types/common.types';
import { eCommercePermissions } from './types/account.types';
import { formatDateToReadableString } from './utils/formatting';
import { PreOrderData, PreOrderedProduct, PreOrderPayloadProduct, Product } from '../product/product.types';

@Injectable({
  providedIn: 'root'
})
export class OrderService {
  private apiService = inject(ApiService);
  private firebaseCrudService = inject(FirebaseCrudService);
  private localStorageService = inject(LocalStorageService);
  private notificationService = inject(NotificationService);
  private deliveriesService = inject(DeliveriesService);
  private stockService = inject(StockService);
  private modalContentService = inject(ModalContentService);

  endpoints = {
    order: `/order`,
    coupon: '/account/coupons',
    ecommerceLogs: '/ecommerce-logs',
    preOrder: '/pre-order',
  };

  odooOrder: WritableSignal<OrderResponse | any> = signal(null)

  showCouponErrorMessage: WritableSignal<boolean> = signal(false);

  couponErrorMessage: WritableSignal<string> = signal('');

  constructor() { }

  getOrder(marketStatus?: WritableSignal<MarketStatus>) {

    const sessionStored: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    const permissions: eCommercePermissions | null = this.localStorageService.get(LOCALSTORAGE_KEYS.PERMISSIONS);
    if (!sessionStored || (!sessionStored.settings?.hasPaymentMethod && permissions?.settings.paymentMethod.allowed)) return;

    const params: RequestHandlerParams = {
      endpoint: this.endpoints.order,
      method: 'GET',
      apiV3: true
    }

    return this.apiService.handleRequest<ApiResponse<any>>(params).pipe(
      tap((response: any) => {
        if (!response)
          throw new Error('getOrder Error');

        const order = response?.data;
        this.odooOrder.set(order)
        if (marketStatus && environment.config.flows.shop.validateMarketState && order.marketStatus)
          marketStatus.set(order.marketStatus);
      }),
    ).subscribe();
  }

  private getStock() {
    const suggestedProducts = this.odooOrder()?.relatedProducts?.suggested || [];
    const buyAgainProducts = this.odooOrder()?.relatedProducts?.buyAgain || [];
    const favoriteProducts = this.odooOrder()?.relatedProducts?.favorites || [];

    const allRelatedProducts = [...suggestedProducts, ...buyAgainProducts, ...favoriteProducts];

    if (!allRelatedProducts?.length)
      return

    this.stockService.getStock(undefined, allRelatedProducts.map((p: any) => p.id))
  }

  setUpDateForFirebase() {
    const deliveryInfo = this.deliveriesService.deliveryZoneInfo();
    if (!deliveryInfo) return {};

    let cutoffObject = { date: deliveryInfo.cutoffDate, time: deliveryInfo.cutoffTime }
    if (deliveryInfo.order) {
      cutoffObject.date = deliveryInfo.order.cutoffDate;
      cutoffObject.time = deliveryInfo.order.cutoffTime;
    }

    // Combina las dos cadenas en una sola
    const dateTimeStr = `${cutoffObject.date} ${cutoffObject.time}`;

    // Extraer partes de la fecha y la hora
    const [datePart, time, period] = dateTimeStr.split(/[\s]+/);
    const [hours, minutes] = time.split(':').map(Number);
    let adjustedHours = hours;

    if (period.toLowerCase() === "p.m." && hours !== 12) {
      adjustedHours = hours + 12;
    } else if (period.toLowerCase() === "a.m." && hours === 12) {
      adjustedHours = 0;
    }

    // Crear una cadena en formato ISO y convertirla a UTC
    const formattedDateTimeStr = `${datePart}T${adjustedHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00Z`;

    const utcDate = DateTime.fromISO(formattedDateTimeStr, { zone: 'UTC' });

    // Convertir a milisegundos desde epoch
    const utcMillis = utcDate.toMillis();

    return { cutoffDate: utcMillis, lastUpdate: Date.now(), isNotYetSavedEventGenerated: false };
  }

  updateOrder(data: UpdateOrderParams, isSubscription: boolean, showDefaultMessage = true): Observable<ApiResponse<any>> {

    try {
      this.#disableCouponErrorMessage();

      const sessionStored: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
      if (!sessionStored || !Object.keys(sessionStored).length)
        throw new Error("ERROR: SESSION NOT FOUNDED");

      const product = this.setUpOrderProducts(data.product as any, isSubscription);

      const bodyRequest: any = {
        products: [product]
      };

      const method = data.orderId ? 'PATCH' : 'POST';

      if (!data.orderId)
        bodyRequest.deliveryDate = data.deliveryDate;

      const params: RequestHandlerParams = {
        endpoint: data.orderId ? `${this.endpoints.order}/${data.orderId}` : this.endpoints.order,
        method,
        body: bodyRequest,
        apiV3: true,
        returnError: true,
        showErrorMessage: false
      }

      return this.apiService.handleRequest<ApiResponse<any>>(params).pipe(
        filter(response => {
          if (!response.error) return true;

          // const c = coupon ? coupon() : null;
          // if (c)
          //   this.#enableCouponErrorMessage(response.message);

          if (response.status === 400)
            this.#updateOrderErrorResponseHandler(response.data, response.message, sessionStored);
          else
            this.notificationService.show({ text: response.message || 'An error occurred while trying to save the order information.', type: 'error' });
          return false;
        }),
        tap((response: any) => {
          this.updateOrderResponseHandler(response, showDefaultMessage);
        }),
      )

    } catch (error) {
      return of<ApiResponse<any>>();
    }
  }

  private updateOrderResponseHandler(response: ApiResponse<any>, showDefaultMessage: boolean = true) {

    if (response.data.order) {
      this.odooOrder.update(() => response.data.order);
    } else {
      this.odooOrder.update(() => response.data);
    }

    if (response?.data?.previousOrder?.isLockedByCutOffDate)
      return this.openModalPastCutOff();

    const hasOutOfStockProducts = this.validateOutOfStockResponse(this.odooOrder()?.outOfStockProducts);

    const hasUnavailableProducts = this.#validateUnavailableProducts(this.odooOrder()?.notAvailableProducts);

    if (showDefaultMessage && !hasOutOfStockProducts && !hasUnavailableProducts)
      this.notificationService.show({ text: 'Order updated successfully', type: 'success' });

    if (response.data?.hasCouponError)
      return this.#enableCouponErrorMessage(response.message);
  }

  #validateUnavailableProducts(products: { name: string, variantName: string, productExists: boolean }[]) {
    if (!products?.length) return false;
    const unavailable = products.map(p => {
      return {
        productName: p.name,
        productVariant: p.variantName,
        outOfStock: true,
        limitExceeded: false
      };
    });

    this.modalContentService.openModal(ModalContentTypes.BUY_AGAIN_WARNING, {
      title: `Update To Product Availability`,
      textContent: `Hi, unfortunately, the following product(s) are no longer available for purchase. Please review the table below to see what we've updated.`,
      orderHistoryWarning: { products: unavailable, showThirdColumn: true, fromOrder: true }
    })
    return true;
  }

  #updateOrderErrorResponseHandler(data: any, message: any, sessionStored: Session) {

    if (!data?.outOfStockProducts?.length)
      return this.notificationService.show({ text: message || 'An error occurred while trying to save the order information.', type: 'error' });

    const oosProducts: { name: string, variantName: string, variantId: number, isSubscription: boolean, bundleId: number, productExists: boolean, reason: string, previousQuantity: number }[] = data.outOfStockProducts;
    this.validateOutOfStockResponse(oosProducts, true);
  }

  private validateOutOfStockResponse(products: { name: string, variantName: string, productExists: boolean, previousQuantity: number }[], error: boolean = false) {
    if (!products?.length) return false;
    const outOfStock = products.map(p => {
      return {
        productName: p.name,
        productVariant: p.variantName,
        outOfStock: !p.productExists,
        limitExceeded: p.productExists,
        previousQuantity: p.previousQuantity || 0
      };
    });

    const title =
      error
        ? 'Order Below Minimum Requirement'
        : 'Update To Product Availability';
    const textContent =
      error
        ? 'Hi, we noticed that your order no longer meets the minimum required for delivery due to insufficient inventory for some items in your cart. Please review the table below for details on the changes and adjust your order accordingly. Once updated, feel free to try again. Thank you for understanding!'
        : `Hi, unfortunately, we don’t have enough inventory for the product below:`;

    this.modalContentService.openModal(ModalContentTypes.BUY_AGAIN_WARNING, {
      title,
      textContent,
      orderHistoryWarning: { products: outOfStock, showThirdColumn: true, fromOrder: true }
    })
    return true;
  }

  private setUpOrderProducts(product: Product, isSubscription: boolean): ProductOrderToSave {

    let productBundle = null;

    if (product.bundle && product.bundle.items.length) {
      productBundle = product.bundle.items
        .filter(bundleProdut => !bundleProdut.isRemoved)
        .map(bundleProduct => ({
          id: bundleProduct.id,
          quantity: +bundleProduct.quantity,
          packageId: bundleProduct.packageId || null
        }));
    }

    let productSubscription = null;

    if (isSubscription && product.subscription) {
      productSubscription = {
        startDate: product?.subscription?.startDate || null,
        frequencyId: product?.subscription?.frequency?.id
      };
    }

    return {
      bundle: productBundle,
      id: product.id,
      quantity: product.quantity ? +product.quantity : 0,
      packageId: product.packageId ?? null,
      subscription: productSubscription,
      variantId: product.variantId || null,
      bundleQuantityChanged: product.bundleQuantityChanged,
    };
  }

  getSession(): Session | null {
    const session: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    if (!session || !Object.keys(session).length) {
      throw new Error("ERROR: SESSION NOT FOUNDED");
    }
    return session;
  }

  updateProductFromFirebase(firebaseOrder: any, productKeyType: string, mapKey: number | string, productFromCard: any) {

    const firebaseProductsMap = arrayToMapMultiKey(['variant.id', 'package.id'], firebaseOrder.products[productKeyType]);
    // If the product has packages, let's search by package id:
    if (productFromCard.package?.id)
      mapKey = `${mapKey}_${productFromCard.package?.id}`
    if (firebaseProductsMap && firebaseProductsMap.has(mapKey)) {
      const orderProduct = firebaseProductsMap.get(mapKey);
      orderProduct.quantity += productFromCard.quantity;
      orderProduct.subscription = productFromCard.subscription;
      firebaseOrder.products[productKeyType] = mapToArray(firebaseProductsMap);
      return true;
    }


    return false;
  }

  updateProductFromOdoo(odooOrder: any, firebaseOrder: any, productKeyType: string, mapKey: number | string, productFromCard: any, overWriteOdooQuantity: boolean = false) {

    if (odooOrder?.products?.[productKeyType]) {

      const odooProductsMap = arrayToMapMultiKey(['variant.id', 'package.id'], odooOrder.products[productKeyType]);

      // If the product has packages, let's search by package id:

      if (productFromCard.package?.id)
        mapKey = `${mapKey}_${productFromCard.package?.id}`

      if (odooProductsMap && odooProductsMap.has(mapKey)) {

        const orderProduct = JSON.parse(JSON.stringify(odooProductsMap.get(mapKey)));

        if (overWriteOdooQuantity)
          orderProduct.quantity = productFromCard.quantity;
        else
          orderProduct.quantity += productFromCard.quantity;

        orderProduct.variant = {
          variantId: orderProduct.variantId,
          ...productFromCard.variant,
          ...orderProduct.variant
        };

        firebaseOrder.products[productKeyType].push(orderProduct);
        return true;
      }
    }
    return false;
  }

  deleteProductOrder(orderId: number, lineId: number) {
    const sessionStored: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    if (!sessionStored || !Object.keys(sessionStored).length)
      throw new Error("ERROR: SESSION NOT FOUNDED");

    const params: RequestHandlerParams = {
      endpoint: this.endpoints.order + `/${orderId}/line/${lineId}`,
      method: 'DELETE',
      apiV3: true
    }

    return this.apiService.handleRequest<ApiResponse<any>>(params).pipe(
      tap((response: any) => {
        if (response?.data?.previousOrder?.isLockedByCutOffDate)
          return this.openModalPastCutOff()

        this.notificationService.show({ text: 'Product removed from order successfully', type: 'success' });
        this.odooOrder.set(response?.data?.order);
        this.getStock()
      }),
    )
  }

  applyCouponWithOrder(coupon: WritableSignal<string>, orderId: number) {
    const sessionStored: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    this.#disableCouponErrorMessage();
    if (!sessionStored || !Object.keys(sessionStored).length)
      throw new Error("ERROR: SESSION NOT FOUNDED");

    const cleanedCoupon = coupon().trim();
    const params: RequestHandlerParams = {
      endpoint: this.endpoints.coupon,
      method: 'POST',
      body: { coupon: cleanedCoupon, orderId },
      apiV3: true,
      returnError: true,
      showErrorMessage: false
    }

    return this.apiService.handleRequest<ApiResponse<any>>(params).pipe(
      filter(response => {
        if (!response.error) return true;
        this.#enableCouponErrorMessage(response.message);
        return false;
      }),
      tap((response: any) => {
        coupon.set('');
        this.notificationService.show({ text: response?.message, type: 'success' });
        this.odooOrder.set(response?.data);
      }),
    )
  }

  skipOrder(args: {
    orderId: number;
    isSkipping: boolean;
    donationAmount: number | any
  }) {
    const sessionStored: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    if (!sessionStored || !Object.keys(sessionStored).length)
      throw new Error("ERROR: SESSION NOT FOUNDED");

    const { orderId, isSkipping, donationAmount } = args;
    const donation = !donationAmount ? null : {
      amount: donationAmount,
      isRecurrent: false,
    }
    const params: RequestHandlerParams = {
      endpoint: this.endpoints.order + `/${orderId}/skip`,
      method: 'POST',
      body: {
        isSkipping,
        donation
      },
      apiV3: true
    }

    return this.apiService.handleRequest<ApiResponse<any>>(params).pipe(
      tap((response: any) => {
        if (response?.data?.previousOrder?.isLockedByCutOffDate)
          return this.openModalPastCutOff()

        const actionPhrase = isSkipping ? 'applied' : 'removed';
        this.notificationService.show({ text: `Order skip ${actionPhrase} successfully`, type: 'success' });
        this.odooOrder.set(response?.data?.order);
      }),

      tap(() => (this.deliveriesService.getDeliveryZoneInfo().subscribe())),
    )
  }

  private openModalPastCutOff() {
    this.modalContentService.openModal(ModalContentTypes.PAST_CUT_OFF, { closeable: true });
  }

  setupProductForKlaviyo(product: any) {
    return {
      bundle: product.bundle?.items || [],
      category: product.category,
      id: product.id,
      name: product.name,
      price: product.price,
      productUrl: product.productUrl,
      size: product.size || '',
      subcategory: product.subCategory,
      originalPrice: product.originalPrice,
      producer: product.producer,
      specialCategory: product.specialCategory,
      tags: product.tags,
      variant: product.variant,
      package: product.package
    }
  }

  calculateProductTotalPrice(product: any): number {
    let totalPrice = 0;
    if (product?.bundle?.items) {
      if (product.bundle.isFixed) {
        totalPrice = +product.price;
        const premiumItems = product?.bundle?.items?.filter((pi: any) => pi.isPremiumAddon);
        if (premiumItems?.length) {
          totalPrice += premiumItems.reduce((total: number, bundleItem: any) => {
            const bundleItemPrice = (+bundleItem?.price * +bundleItem?.quantity) || 0
            return total + bundleItemPrice
          }, 0);
        }
      } else if (product?.bundle?.items?.length)
        totalPrice = product.bundle.items.reduce((total: number, bundleItem: any) => {
          const bundleItemPrice = (bundleItem?.isRemoved ? 0 : (+bundleItem?.price * +bundleItem?.quantity) || 0)
          return total + bundleItemPrice
        }, 0);
    }
    if (totalPrice === 0)
      totalPrice = product.price

    return +((totalPrice * product.quantity).toFixed(2));
  }

  private checkPendingChanges(products: any) {
    return !!(products &&
      [...products?.common, ...products?.subscription].some(product => product.hasPendingChanges));
  }

  savePreOrderProduct(preOrderData: PreOrderPayloadProduct) {
    const params: RequestHandlerParams = {
      endpoint: `${this.endpoints.order}${this.endpoints.preOrder}`,
      method: 'POST',
      apiV3: true,
      body: preOrderData
    };

    return this.apiService.handleRequest(params);
  }

  getPreOrders() {
    const params: RequestHandlerParams = {
      endpoint: `${this.endpoints.order}${this.endpoints.preOrder}`,
      method: 'GET',
      apiV3: true,
      showErrorMessage: false
    };

    return this.apiService.handleRequest<ApiResponse<PreOrderedProduct[]>>(params).pipe(
      filter((data) => !!data.data.length),
      map((data) => {
        const res = data.data.map(p => {
          p.preOrder = this.#setUpPreorderProductData(p.preOrder, p.quantity);
          return p;
        })

        return res;
      })
    )
  }

  #setUpPreorderProductData(preOrder: PreOrderData | null, quantity: number): PreOrderData | null {
    if (!preOrder) return null;
    return {
      ...preOrder,
      _readableEndDate: formatDateToReadableString(preOrder.endDate),
      _readableStartDeliveryDate: formatDateToReadableString(preOrder.startDeliveryDate),
      _readableEndDeliveryDate: formatDateToReadableString(preOrder.endDeliveryDate),
      deposit: preOrder.deposit * quantity
    } as PreOrderData
  }

  cancelOrderById(orderId: number) {
    const params: RequestHandlerParams = {
      endpoint: `${this.endpoints.order}/${orderId}`,
      method: 'DELETE',
      apiV3: true,
      isVoid: true
    };

    return this.apiService.handleRequest<void>(params).pipe(
      tap(() => this.getOrder()),
      tap(() => this.deliveriesService.getDeliveryZoneInfo().subscribe()),
      tap(() => this.notificationService.show({ text: 'Your order has been successfully cancelled', type: 'success' }))
    );
  }

  getBundlesLikeSubscriptions(): ShopBundleList[] {

    const odooOrder = this.odooOrder();

    if (!odooOrder || !odooOrder.products || !odooOrder.products.subscription)
      return [];

    return odooOrder
      .products
      .subscription
      .filter((x: any) => x.bundle.items.length && !x.bundle.isLocked)
      .map((x: any) => ({
        frequency: x.subscription.frequency.name,
        name: x.name,
        id: x.id,
        image: x.img,
        price: this.calculateProductTotalPrice(x)
      }));
  }

  #enableCouponErrorMessage(message?: string) {
    this.showCouponErrorMessage.set(true);
    this.couponErrorMessage.set(message || 'There was an error trying to apply the coupon');
    setTimeout(() => {
      this.#disableCouponErrorMessage()
    }, 10 * 1000);
  }

  #disableCouponErrorMessage() {
    this.showCouponErrorMessage.set(false);
    this.couponErrorMessage.set('');
  }
}
